const MAX_NOTEBOOK_PAYLOAD_BYTES = 1_500_000;

function utf8ByteLength(value) {
  const text = String(value || '');
  if (typeof TextEncoder === 'function') {
    return new TextEncoder().encode(text).length;
  }
  return unescape(encodeURIComponent(text)).length;
}

function jsonReplacer() {
  const seen = new WeakSet();
  return function replace(_key, value) {
    if (typeof value === 'function' || typeof value === 'symbol' || value === undefined) {
      return undefined;
    }
    if (typeof value === 'bigint') {
      return String(value);
    }
    if (ArrayBuffer.isView(value) && !(value instanceof DataView)) {
      return Array.from(value);
    }
    if (value && typeof value === 'object') {
      if (seen.has(value)) {
        return undefined;
      }
      seen.add(value);
    }
    return value;
  };
}

export function serializeNotebookFigure(plotlyPayload) {
  const payload =
    plotlyPayload && typeof plotlyPayload === 'object'
      ? {
          data: Array.isArray(plotlyPayload.data) ? plotlyPayload.data : [],
          layout:
            plotlyPayload.layout && typeof plotlyPayload.layout === 'object'
              ? plotlyPayload.layout
              : {},
          config:
            plotlyPayload.config && typeof plotlyPayload.config === 'object'
              ? plotlyPayload.config
              : {}
        }
      : null;
  if (!payload || payload.data.length === 0) {
    throw new Error('No chart data is available for notebook export.');
  }
  const serialized = JSON.stringify(payload, jsonReplacer());
  if (!serialized) {
    throw new Error('The chart could not be serialized for notebook export.');
  }
  if (utf8ByteLength(serialized) > MAX_NOTEBOOK_PAYLOAD_BYTES) {
    throw new Error('This chart is too large for notebook export. Filter or aggregate the data first.');
  }
  return serialized;
}

function safeTitle(value) {
  const title = String(value || '').replace(/\s+/g, ' ').trim();
  return title.slice(0, 160) || 'Helix chart analysis';
}

function safeQuery(value) {
  return String(value || '').trim().slice(0, 20_000);
}

function markdownText(title, query) {
  const lines = [`# ${safeTitle(title)}`, '', 'Exported from Helix.'];
  if (query) {
    lines.push('', '## Source request', '', `> ${safeQuery(query).replace(/\n/g, '\n> ')}`);
  }
  return lines.join('\n');
}

const CHART_ROWS_PYTHON = `def chart_rows(figure):
    rows = []
    for trace_index, trace in enumerate(figure.get("data", [])):
        series_name = trace.get("name") or f"trace_{trace_index + 1}"
        x_values = trace.get("x") or trace.get("labels") or []
        y_values = trace.get("y") or trace.get("values") or []
        if not isinstance(x_values, list):
            x_values = list(x_values)
        if not isinstance(y_values, list):
            y_values = list(y_values)
        row_count = max(len(x_values), len(y_values))
        for row_index in range(row_count):
            rows.append({
                "trace": series_name,
                "x": x_values[row_index] if row_index < len(x_values) else None,
                "y": y_values[row_index] if row_index < len(y_values) else None,
            })
    return rows`;

function codeCell(source) {
  return {
    cell_type: 'code',
    execution_count: null,
    metadata: {},
    outputs: [],
    source: String(source).split(/(?<=\n)/)
  };
}

function markdownCell(source) {
  return {
    cell_type: 'markdown',
    metadata: {},
    source: String(source).split(/(?<=\n)/)
  };
}

function codeFromExamples(examples) {
  const list = Array.isArray(examples) ? examples : examples ? [examples] : [];
  return list
    .map(item => (typeof item === 'string' ? item : item && typeof item.code === 'string' ? item.code : ''))
    .filter(Boolean)
    .join('\n\n');
}

export function buildServerNotebookSpec({ title, query, plotly } = {}) {
  const figure = JSON.parse(serializeNotebookFigure(plotly));
  const metadata = (plotly && plotly.metadata) || {};
  const visualization = (metadata.code_summary && metadata.code_summary.visualization) || {};
  const narrative = [plotly && plotly.verbalization, metadata.verbalization].find(
    value => typeof value === 'string' && value.trim()
  );
  return {
    title: String(title || 'Chart'),
    query: String(query || ''),
    narrative: narrative || '',
    code: codeFromExamples(visualization.code_examples),
    figure
  };
}

async function postNotebookExport(body, fetchImpl) {
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) return null;
  try {
    const response = await doFetch('/api/notebooks/export.ipynb', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response || !response.ok) return null;
    const text = await response.text();
    const parsed = JSON.parse(text);
    return parsed && parsed.nbformat === 4 && Array.isArray(parsed.cells) ? text : null;
  } catch (_error) {
    return null;
  }
}

export async function fetchServerIpynb(context, fetchImpl) {
  let spec;
  try {
    spec = buildServerNotebookSpec(context);
  } catch (_error) {
    return null;
  }
  return postNotebookExport({ graph: spec }, fetchImpl);
}

export async function fetchServerDashboardIpynb(title, contexts, fetchImpl) {
  const graphs = [];
  for (const context of contexts || []) {
    try {
      graphs.push(buildServerNotebookSpec(context));
    } catch (_error) {
      // A chart without renderable data is skipped; the rest still export.
    }
  }
  if (graphs.length === 0) return null;
  return postNotebookExport({ title: String(title || 'Dashboard'), graphs }, fetchImpl);
}

export function buildIpynbNotebook({ title, query, plotly } = {}) {
  const serializedFigure = serializeNotebookFigure(plotly);
  const figureLiteral = JSON.stringify(serializedFigure);
  const notebook = {
    cells: [
      markdownCell(markdownText(title, query)),
      codeCell(`import json\nimport pandas as pd\n\nfigure_spec = json.loads(${figureLiteral})`),
      codeCell(`${CHART_ROWS_PYTHON}\n\nchart_data = pd.DataFrame(chart_rows(figure_spec))\nchart_data`),
      codeCell(
        `try:\n    import plotly.io as pio\n    figure = pio.from_json(json.dumps(figure_spec))\n    figure.show()\nexcept ImportError:\n    print("Install plotly to render the interactive chart: pip install plotly")`
      )
    ],
    metadata: {
      kernelspec: {
        display_name: 'Python 3',
        language: 'python',
        name: 'python3'
      },
      language_info: {
        name: 'python',
        version: '3'
      },
      helix: {
        source_query: safeQuery(query),
        export_type: 'chart'
      }
    },
    nbformat: 4,
    nbformat_minor: 5
  };
  return JSON.stringify(notebook, null, 2);
}

function indentPython(source, spaces = 4) {
  const prefix = ' '.repeat(spaces);
  return String(source)
    .split('\n')
    .map(line => `${prefix}${line}`)
    .join('\n');
}

function escapeMarimoMarkdown(value) {
  return String(value || '').replace(/\\/g, '\\\\').replace(/"""/g, '\\"\\"\\"');
}

export function buildMarimoNotebook({ title, query, plotly } = {}) {
  const serializedFigure = serializeNotebookFigure(plotly);
  const figureLiteral = JSON.stringify(serializedFigure);
  const markdown = escapeMarimoMarkdown(markdownText(title, query));
  return `import marimo as mo

__generated_with = "0.10.19"
app = marimo.App()


@app.cell
def _():
    mo.md("""
${indentPython(markdown, 4)}
    """)
    return


@app.cell
def _():
    import json
    import pandas as pd
    figure_spec = json.loads(${figureLiteral})
    return figure_spec, json, pd


@app.cell
def _(figure_spec, pd):
${indentPython(CHART_ROWS_PYTHON, 4)}

    chart_data = pd.DataFrame(chart_rows(figure_spec))
    chart_data
    return chart_data


@app.cell
def _(figure_spec):
    layout_title = (figure_spec.get("layout") or {}).get("title", "")
    if isinstance(layout_title, dict):
        layout_title = layout_title.get("text", "")
    chart_summary = {
        "chart_type": (figure_spec.get("data") or [{}])[0].get("type", "scatter"),
        "trace_count": len(figure_spec.get("data") or []),
        "title": layout_title,
    }
    chart_summary
    return chart_summary


if __name__ == "__main__":
    app.run()
`;
}

export function notebookFilename(title, extension) {
  const slug = safeTitle(title)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  const safeExtension = String(extension || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  return `${slug || 'helix-notebook'}.${safeExtension || 'txt'}`;
}

export function downloadNotebookText(text, filename, mimeType = 'text/plain;charset=utf-8') {
  const blob = new Blob([String(text || '')], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export { MAX_NOTEBOOK_PAYLOAD_BYTES };
